import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { suppliersApi, contactsApi } from '@/services/api';
import { Supplier, Contact } from '@/types';
import EditSupplierForm from '@/components/EditSupplierForm';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const SupplierDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [error, setError] = useState<string>('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string>('');

  const { data: response, isLoading, error: queryError } = useQuery(
    ['supplier', id],
    () => suppliersApi.getById(id!),
    {
      enabled: !!id,
      onError: (error) => {
        setError('Failed to load supplier details');
        console.error('Supplier query error:', error);
      },
    }
  );

  // Fetch contacts for this supplier
  const { data: contactsResponse, isLoading: isLoadingContacts } = useQuery(
    ['supplier-contacts', id],
    () => contactsApi.getBySupplier(id!),
    {
      enabled: !!id,
      retry: 1,
      onError: (error) => {
        console.error('Contacts query error:', error);
      },
    }
  );

  // CR-19: cross-project performance metrics for this supplier
  const { data: metricsResponse } = useQuery(
    ['supplier-metrics', id],
    () => suppliersApi.getMetrics(id!),
    {
      enabled: !!id,
      onError: (error) => {
        console.error('Supplier metrics query error:', error);
      },
    }
  );
  const metrics = metricsResponse?.data as {
    total: number;
    successes: number;
    fails: number;
    geoFails: number;
    dupFails: number;
    geoFailPct: number;
    dupFailPct: number;
    avgThreatOverall: number;
    avgThreatSuccesses: number;
  } | undefined;

  const deleteSupplierMutation = useMutation(suppliersApi.delete, {
    onSuccess: () => {
      navigate('/admin/suppliers');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete supplier');
    },
  });

  const deleteContactMutation = useMutation(contactsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(['supplier-contacts', id]);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete contact');
    },
  });

  const supplier = response?.data as Supplier;
  const contacts = contactsResponse?.data || [];

  const isDataLoading = isLoading || isLoadingContacts;


  const handleDeleteSupplier = () => {
    if (supplier && window.confirm(`Are you sure you want to delete "${supplier.name}"? This action cannot be undone.`)) {
      deleteSupplierMutation.mutate(supplier.id);
    }
  };

  const handleDeleteContact = (contactId: string, contactName: string) => {
    if (window.confirm(`Are you sure you want to delete "${contactName}"?`)) {
      deleteContactMutation.mutate(contactId);
    }
  };

  const handleEditSupplier = () => {
    setEditError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditError('');
    setIsEditing(false);
  };

  const handleSaveSupplier = async (updateData: any) => {
    try {
      console.log('Sending update request for supplier:', supplier!.id);
      console.log('Update data:', updateData);
      const result = await suppliersApi.update(supplier!.id, updateData);
      console.log('Update response:', result);
      if (result.success) {
        setIsEditing(false);
        queryClient.invalidateQueries(['supplier', id]);
      } else {
        console.error('Update failed:', result);
        setEditError(result.error || 'Failed to update supplier');
      }
    } catch (error: any) {
      console.error('Update error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Validation details:', error.response?.data?.details);
      if (error.response?.data?.details && Array.isArray(error.response?.data?.details)) {
        error.response.data.details.forEach((detail: any, index: number) => {
          console.error(`Validation error ${index + 1}:`, detail);
        });
        const errorMessages = error.response.data.details.map((detail: any) => {
          if (detail.path && detail.path.length) {
            return `${detail.path.join('.')}: ${detail.message}`;
          } else if (detail.field) {
            return `${detail.field}: ${detail.message}`;
          } else {
            return `${detail.message || 'Unknown validation error'}`;
          }
        }).join('; ');
        setEditError(`Validation failed: ${errorMessages}`);
      } else {
        setEditError(error.response?.data?.error || error.response?.data?.details || 'Failed to update supplier');
      }
    }
  };

  if (isDataLoading) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div className="h-5 w-40 md-skeleton mb-3"></div>
        <div className="h-9 w-64 md-skeleton mb-2"></div>
        <div className="h-5 w-48 md-skeleton mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md-card p-6">
            <div className="h-7 md-skeleton w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 md-skeleton"></div>
              ))}
            </div>
          </div>
          <div className="md-card p-6">
            <div className="h-7 md-skeleton w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-4 md-skeleton"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || queryError) {
    return (
      <div className="text-center py-20 animate-fade-in-up">
        <div className="text-[#EF4444] text-lg mb-4 font-medium">
          {(error as any) || (queryError as any) || 'An error occurred while loading supplier details'}
        </div>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="md-btn md-btn-primary"
          >
            Refresh
          </button>
          <button
            onClick={() => navigate('/admin/suppliers')}
            className="md-btn md-btn-outlined"
          >
            Back to Suppliers
          </button>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="text-center py-20 animate-fade-in-up">
        <div className="text-[#EF4444] text-lg mb-4 font-medium">
          Supplier not found
        </div>
        <button
          onClick={() => navigate('/admin/suppliers')}
          className="md-btn md-btn-primary"
        >
          Back to Suppliers
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start animate-fade-in-up">
        <div>
          <Link
            to="/admin/suppliers"
            className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#18A5F9] text-sm font-semibold mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Suppliers
          </Link>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">{supplier.name}</h1>
          <p className="text-[#9CA3AF] text-sm">ID: <span className="font-mono font-semibold text-[#6B7280]">{supplier.id}</span></p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={handleEditSupplier}
              className="md-btn md-btn-outlined text-[13px]"
            >
              Edit Supplier
            </button>
          ) : (
            <button
              onClick={handleCancelEdit}
              className="md-btn md-btn-outlined text-[13px]"
            >
              Cancel
            </button>
          )}
          <button
            onClick={() => setShowContactModal(true)}
            className="md-btn md-btn-primary text-[13px]"
          >
            Add Contact
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleDeleteSupplier}
              className="md-btn md-btn-danger text-[13px]"
              disabled={deleteSupplierMutation.isLoading}
            >
              {deleteSupplierMutation.isLoading ? 'Deleting...' : 'Delete Supplier'}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {(error || queryError) && (
        <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error || 'Failed to load supplier details. Please try again.'}</div>
      )}

      <div className="space-y-6">
        {/* Supplier Details */}
        <div className="md-card p-6 animate-fade-in-up stagger-1">
          <h2 className="text-xl font-bold font-heading text-[#1A1F2B] mb-6">Supplier Details</h2>

          {editError && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl mb-4">{editError}</div>
          )}

          {isEditing ? (
            <EditSupplierForm
              supplier={supplier}
              onSave={handleSaveSupplier}
              onCancel={handleCancelEdit}
            />
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Name</label>
                <p className="text-[15px] font-semibold text-[#374151]">{supplier.name}</p>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Placeholder Token</label>
                <p className="text-[15px] font-semibold text-[#374151]">{supplier.placeholderToken || 'System Default'}</p>
              </div>

              {supplier.completeUrlTemplate && (
                <div>
                  <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Complete URL</label>
                  <p className="text-sm font-mono bg-[#F9FAFB] p-2.5 rounded-xl break-all border border-[#ECEEF1] text-[#6B7280]">
                    {supplier.completeUrlTemplate}
                  </p>
                </div>
              )}

              {supplier.terminateUrlTemplate && (
                <div>
                  <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Terminate URL</label>
                  <p className="text-sm font-mono bg-[#F9FAFB] p-2.5 rounded-xl break-all border border-[#ECEEF1] text-[#6B7280]">
                    {supplier.terminateUrlTemplate}
                  </p>
                </div>
              )}

              {supplier.quotaFullUrlTemplate && (
                <div>
                  <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Quota Full URL</label>
                  <p className="text-sm font-mono bg-[#F9FAFB] p-2.5 rounded-xl break-all border border-[#ECEEF1] text-[#6B7280]">
                    {supplier.quotaFullUrlTemplate}
                  </p>
                </div>
              )}

              {supplier.securityTerminateUrlTemplate && (
                <div>
                  <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Security URL</label>
                  <p className="text-sm font-mono bg-[#F9FAFB] p-2.5 rounded-xl break-all border border-[#ECEEF1] text-[#6B7280]">
                    {supplier.securityTerminateUrlTemplate}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Created</label>
                <p className="text-[15px] font-semibold text-[#374151]">{formatDate(supplier.createdAt)}</p>
              </div>

              <div>
                <label className="block text-[13px] font-bold text-[#9CA3AF] mb-1 uppercase tracking-wider">Last Updated</label>
                <p className="text-[15px] font-semibold text-[#374151]">{formatDate(supplier.updatedAt)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Research Defender Settings - Separate Card */}
        {!isEditing && (
          <div className="bg-white border border-black rounded-lg p-6">
            <h2 className="text-2xl font-bold font-heading mb-6 text-gray-900">Research Defender Settings</h2>
            <p className="text-sm text-gray-500 mb-4">Default settings for all collections using this supplier. Collections can override these values.</p>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600">RD Enabled</label>
                <p className="text-lg mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm ${supplier.rdEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {supplier.rdEnabled ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Block Duplicates</label>
                <p className="text-lg mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm ${supplier.rdBlockDuplicates ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {supplier.rdBlockDuplicates ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Geo Blocker</label>
                <p className="text-lg mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm ${supplier.enableGeoBlocker ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {supplier.enableGeoBlocker ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600">Trigger Score</label>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        supplier.rdTriggerScore <= 30 ? 'bg-red-500' :
                        supplier.rdTriggerScore <= 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${supplier.rdTriggerScore}%` }}
                    />
                  </div>
                  <span className="text-2xl font-bold w-16 text-right">{supplier.rdTriggerScore}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {supplier.rdTriggerScore <= 30 ? 'Strict - Most respondents will be blocked' :
                   supplier.rdTriggerScore <= 60 ? 'Moderate - Some respondents may be blocked' :
                   'Lenient - Only high-risk respondents blocked'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CR-19: Supplier performance across all projects */}
        <div className="md-card p-6 animate-fade-in-up stagger-2">
          <h2 className="text-xl font-bold font-heading text-[#1A1F2B] mb-1">Performance</h2>
          <p className="text-sm text-[#9CA3AF] mb-5">Across every project this supplier has been used in.</p>
          {!metrics || metrics.total === 0 ? (
            <p className="text-sm text-[#9CA3AF]">No respondent activity recorded for this supplier yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Total</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">{metrics.total}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <div className="text-[11px] text-emerald-700 uppercase tracking-wider font-bold mb-1">Successes</div>
                  <div className="text-xl font-bold text-emerald-700">{metrics.successes}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <div className="text-[11px] text-red-700 uppercase tracking-wider font-bold mb-1">Fails</div>
                  <div className="text-xl font-bold text-red-700">{metrics.fails}</div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Success Rate</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">
                    {metrics.total > 0 ? Math.round((metrics.successes / metrics.total) * 100) : 0}%
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Geo Fails</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">{metrics.geoFailPct}%</div>
                  <div className="text-[11px] text-[#9CA3AF]">{metrics.geoFails} of {metrics.total}</div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Duplicate Fails</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">{metrics.dupFailPct}%</div>
                  <div className="text-[11px] text-[#9CA3AF]">{metrics.dupFails} of {metrics.total}</div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Avg Threat</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">{metrics.avgThreatOverall}</div>
                  <div className="text-[11px] text-[#9CA3AF]">all respondents</div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-3 text-center">
                  <div className="text-[11px] text-[#9CA3AF] uppercase tracking-wider font-bold mb-1">Avg Threat</div>
                  <div className="text-xl font-bold text-[#1A1F2B]">{metrics.avgThreatSuccesses}</div>
                  <div className="text-[11px] text-[#9CA3AF]">successes only</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Contacts */}
        <div className="md-card p-6 animate-fade-in-up stagger-2">
          <h2 className="text-xl font-bold font-heading text-[#1A1F2B] mb-6">Contacts ({contacts?.length || 0})</h2>

          {(!contacts || contacts.length === 0) ? (
            <div className="text-center py-8">
              <p className="text-[#6B7280] mb-4">No contacts yet</p>
              <button
                onClick={() => setShowContactModal(true)}
                className="md-btn md-btn-primary text-[13px]"
              >
                Add First Contact
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-[#F9FAFB] border border-[#ECEEF1] rounded-xl p-4 hover:border-[#18A5F9]/20 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-[#1A1F2B]">{contact.name}</h3>
                        {contact.isPrimary && (
                          <span className="px-2 py-0.5 bg-[#E8F4FD] text-[#0B7BC7] text-xs rounded-full font-semibold">
                            Primary
                          </span>
                        )}
                      </div>

                      {contact.email && (
                        <p className="text-sm text-[#6B7280] mt-1">
                          <span className="font-semibold text-[#9CA3AF]">Email:</span> {contact.email}
                        </p>
                      )}

                      {contact.phone && (
                        <p className="text-sm text-[#6B7280]">
                          <span className="font-semibold text-[#9CA3AF]">Phone:</span> {contact.phone}
                        </p>
                      )}

                      {contact.role && (
                        <p className="text-sm text-[#6B7280]">
                          <span className="font-semibold text-[#9CA3AF]">Role:</span> {contact.role}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => {/* TODO: Edit contact */}}
                        className="text-[#18A5F9] hover:text-[#0D8FDE] text-[13px] font-semibold transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id, contact.name)}
                        className="text-[#EF4444] hover:text-red-700 text-[13px] font-semibold transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>


      {/* Add Contact Modal */}
      {showContactModal && (
        <AddContactModal
          supplierId={supplier.id}
          onClose={() => setShowContactModal(false)}
          onSuccess={() => {
            setShowContactModal(false);
            queryClient.invalidateQueries(['supplier-contacts', id]);
          }}
        />
      )}
    </div>
  );
};

// Add Contact Modal Component
const AddContactModal = ({
  supplierId,
  onClose,
  onSuccess
}: {
  supplierId: string;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    isPrimary: false,
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await contactsApi.create({
        ...formData,
        supplierId,
      });
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Failed to create contact');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create contact');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Add Contact</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Name *</label>
            <div className="md-outlined-textfield">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Email</label>
            <div className="md-outlined-textfield">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Phone</label>
            <div className="md-outlined-textfield">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Role</label>
            <div className="md-outlined-textfield">
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full"
                placeholder="Account Manager, Technical Lead, etc."
              />
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="isPrimary"
                checked={formData.isPrimary}
                onChange={handleChange}
                className="rounded border-[#E2E5EA] text-[#18A5F9] focus:ring-[#18A5F9]"
              />
              <span className="text-sm font-semibold text-[#374151]">Primary Contact</span>
            </label>
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Creating...' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierDetailPage;
